/* eslint-disable max-lines */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Image from '@base/Image';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import CardCarousel from '@cont/PreSubscribed/CardCarousel';
import { getImage } from '@utils/imageHelper';
import { emptyObject } from '@constants/empty';
import isEmptyObject from '@utils/isEmptyObject';
import { segment } from '@actions/analytics';
import { ACHIEVERS_CAROUSEL_CARD_CLICKED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import H2 from '@base/H2/H2';
import Button from '@base/Button/Button';
import Link from '@base/Link/Link';
import { useIsMobile } from '@stores/AppCommonData';

const Color_TAN_Dark = 'var(--color-i-brown-2)';
const Color_TAN_Ultra_Light = 'var(--color-i-orange-6)';

const SecWrapper = styled.section`
  position: relative;
  margin: auto;
  margin-top: 195px;
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
  width: 1136px;
  height: 610px;
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  background-color: var(--color-base-1);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url(${getImage('achieversCard/web_mainbg.svg')});
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_22B};
    width: 728px;
    padding-right: ${Spacings.SPACING_0B} !important;
  }
  ${mediaQuery.uptoMobile} {
    height: unset;
    width: 100%;
    background-image: unset;
    background-color: var(--color-i-orange-7);
    flex-direction: column;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
    margin: ${Spacings.SPACING_14B} 0px 0px;
  }
`;

const StarsImageWrapper = styled.div`
  position: absolute;
  width: ${Spacings.SPACING_9B};
  height: ${Spacings.SPACING_9B};
  right: ${Spacings.SPACING_4B};
  top: ${Spacings.SPACING_4B};
`;

const HeadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    text-align: left;
    align-items: baseline;
  }
`;

const WrapperHeading = styled(H2)`
  color: ${Color_TAN_Dark};
  ${mediaQuery.uptoMobile} {
    width: 50%;
  }
`;

const WrapperDescription = styled.div`
  color: ${Color_TAN_Dark};
  margin-top: ${Spacings.SPACING_3B};
  font-size: ${Fonts.H5};
  width: 75%;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_2B};
    width: 95%;
  }
`;

const CarouselSection = styled.div`
  margin-top: ${({ $isCommonCta }) =>
    $isCommonCta ? `${Spacings.SPACING_8B}` : `${Spacings.SPACING_13B}`};
  display: flex;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    align-items: left;
    margin-top: ${Spacings.SPACING_4B};
  }
  & > div:first-child {
    ${mediaQuery.uptoMobile} {
      width: 100vw;
      margin-bottom: -${Spacings.SPACING_12B};
      margin-left: -${Spacings.SPACING_4B};
    }
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
    overflow: hidden;
  }
  & .card-carosel-container {
    ${mediaQuery.uptoTablet} {
      padding-bottom: 3.5rem;
    }
    ${mediaQuery.aboveTablet} {
      margin-left: ${Spacings.SPACING_15B};
    }
  }
`;

const CardWapper = styled.div`
  position: relative;
  width: 472px;
  height: 328px;
  background-color: ${Color_TAN_Ultra_Light};
  border-radius: ${Spacings.SPACING_3B};
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background-image: ${({ $src }) => `url(${getImage($src)})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  margin-right: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoTablet} {
    margin-right: ${Spacings.SPACING_3B};
    width: 320px;
    height: 320px;
  }
`;

const CtaButton = styled(Button)`
  background: var(--color-i-brown-2);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_9B};
  margin-top: ${Spacings.SPACING_5B};
  color: var(--color-i-white);
  height: unset;
  min-height: unset;
  font-size: ${Fonts.SMALL};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_7B};
    border-radius: ${Spacings.SPACING_1B};
    font-size: ${Fonts.ULTRA_SMALL};
  }
`;
const CommonCtaButton = styled(CtaButton)`
  ${mediaQuery.uptoMobile} {
    border-radius: ${Spacings.SPACING_2B};
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_9B};
    font-size: ${Fonts.SMALL};
    width: 90%;
    margin-left: -${Spacings.SPACING_4B};
  }
`;

const handleCardClick = (ctaLink, segmentData) => {
  segment.track(ACHIEVERS_CAROUSEL_CARD_CLICKED, {
    ...segmentData
  });
  window.open(ctaLink, '_blank');
};

const renderCardComponts = (isMobile, cards, goalSegmentData) => {
  return cards.map((card) => {
    const { type, data, extraInfo } = card;
    const cardData = data?.[0] ?? emptyObject;
    const imgSrc = isMobile ? 'mobile' : 'web';
    let icon;
    if (cardData.icon) {
      icon = cardData.icon;
    } else {
      const { webImage, mwebImage } = cardData;
      icon = imgSrc === 'web' ? webImage : mwebImage;
    }
    const { ctaLink } = extraInfo ? extraInfo.ctaDetails : {};
    if (!isEmptyObject(card)) {
      return (
        <CardWapper $src={`achieversCard/${imgSrc}_${type}.svg`} key={ctaLink}>
          {ctaLink ? (
            <Link
              disabled
              href={ctaLink}
              onClick={() =>
                handleCardClick(ctaLink, {
                  ...goalSegmentData,
                  card_type: type
                })
              }
            >
              <Image src={icon} layout="fill" alt="Achiever-icon" />
            </Link>
          ) : (
            <Image src={icon} layout="fill" alt="Achiever-icon" />
          )}
        </CardWapper>
      );
    }
    return null;
  });
};

const AchieversCard = ({ block, goalUID }) => {
  const {
    data = [],
    extraBlockInfo: { header, blockHeader, description, ctaDetails = {} },
    type
  } = block;
  const { ctaLabel, ctaLink } = ctaDetails;
  const isMobile = useIsMobile();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const sectionHeader = header || blockHeader;
  if (data?.length) {
    return (
      <SecWrapper>
        {isMobile && (
          <StarsImageWrapper>
            <Image src="achieversCard/stars.svg" alt="" layout="fill" />
          </StarsImageWrapper>
        )}
        <HeadingSection>
          {sectionHeader && <WrapperHeading>{sectionHeader}</WrapperHeading>}
          {description && (
            <WrapperDescription>{description}</WrapperDescription>
          )}
          {!isMobile && ctaLabel && (
            <CtaButton
              onClick={() =>
                handleCardClick(ctaLink, {
                  ...goalSegmentData,
                  card_type: type
                })
              }
              label={ctaLabel}
            />
          )}
        </HeadingSection>
        <CarouselSection $isCommonCta={!!ctaLabel}>
          <CardCarousel step={2} type="dummy" rightShadowDiv={false}>
            {renderCardComponts(isMobile, data, goalSegmentData)}
          </CardCarousel>
        </CarouselSection>
        {isMobile && ctaLabel && (
          <CommonCtaButton
            $fullWidth
            onClick={() =>
              handleCardClick(ctaLink, {
                ...goalSegmentData,
                card_type: type
              })
            }
            label={ctaLabel}
          />
        )}
      </SecWrapper>
    );
  }
};

AchieversCard.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired
};

export default AchieversCard;
